import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Play, Sparkles } from 'lucide-react';
import { Button } from '../ui/button';

const Hero = () => {
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    // Set target date to June 15, 2027 for 1 MILE event
    const targetDate = new Date('2027-06-15T00:00:00');

    const interval = setInterval(() => {
      const now = new Date();
      const diff = targetDate - now;

      if (diff > 0) {
        setCountdown({
          days: Math.floor(diff / (1000 * 60 * 60 * 24)),
          hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((diff % (1000 * 60)) / 1000)
        });
      }
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
        {/* Animated gradient orbs */}
        <div className="absolute top-20 left-10 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-80 h-80 bg-teal-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-purple-500/10 rounded-full blur-3xl"></div>
        
        {/* Grid pattern */}
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0wIDBoNjB2NjBIMHoiLz48cGF0aCBkPSJNMzAgMzBtLTEgMGExIDEgMCAxIDAgMiAwYTEgMSAwIDEgMCAtMiAwIiBmaWxsPSJyZ2JhKDI1NSwyNTUsMjU1LDAuMSkiLz48L2c+PC9zdmc+')] opacity-40"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Content */}
          <div className="text-center lg:text-left">
            {/* Event Countdown Banner */}
            <div className="inline-flex flex-wrap items-center justify-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-3 py-2 mb-6 lg:mb-8 border border-white/20">
              <div className="flex items-center space-x-2">
                <Sparkles className="w-4 h-4 text-yellow-400" />
                <span className="text-xs sm:text-sm text-gray-200">1 MILE 2027</span>
              </div>
              <div className="flex space-x-1 text-white font-mono text-xs sm:text-sm">
                <span className="bg-white/20 px-2 py-0.5 rounded">{countdown.days}d</span>
                <span className="bg-white/20 px-2 py-0.5 rounded">{countdown.hours}h</span>
                <span className="bg-white/20 px-2 py-0.5 rounded">{countdown.minutes}m</span>
                <span className="bg-white/20 px-2 py-0.5 rounded">{countdown.seconds}s</span>
              </div>
            </div>

            <h1 className="text-3xl sm:text-4xl lg:text-6xl font-bold text-white leading-tight mb-4 lg:mb-6">
              Empowering 
              <span className="relative">
                <span className="bg-gradient-to-r from-blue-400 via-teal-400 to-cyan-400 bg-clip-text text-transparent"> Student </span>
                <svg className="absolute -bottom-2 left-0 w-full" viewBox="0 0 200 12" fill="none">
                  <path d="M2 10C50 4 100 4 198 10" stroke="url(#gradient)" strokeWidth="3" strokeLinecap="round"/>
                  <defs>
                    <linearGradient id="gradient" x1="0" y1="0" x2="200" y2="0">
                      <stop stopColor="#60A5FA"/>
                      <stop offset="1" stopColor="#2DD4BF"/>
                    </linearGradient>
                  </defs>
                </svg>
              </span>
              Knowledge
            </h1>

            <p className="text-base lg:text-lg text-blue-100 font-medium tracking-widest mb-3 lg:mb-4">
              S T E A M – I E
            </p>
            <p className="text-xs sm:text-sm text-gray-400 mb-4 lg:mb-6 px-4 lg:px-0">
              Science • Technology • Engineering • Arts • Mathematics – Innovation & Entrepreneurship
            </p>

            <p className="text-base lg:text-lg text-gray-300 mb-6 lg:mb-8 leading-relaxed max-w-xl mx-auto lg:mx-0 px-2 lg:px-0">
              Transform screen time into productive learning. Our coding and robotics programs 
              captivate young minds, fostering a love for technology while building essential 
              skills for the future.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-3 lg:gap-4">
              <Link to="/contact">
                <Button className="bg-gradient-to-r from-blue-600 to-teal-500 hover:from-blue-700 hover:to-teal-600 text-white px-6 lg:px-8 py-4 lg:py-6 text-base lg:text-lg rounded-full shadow-2xl shadow-blue-500/30 hover:shadow-blue-500/50 transition-all duration-300 transform hover:-translate-y-1 group">
                  Get Started Now
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
              <Link to="/events">
                <Button variant="outline" className="border-2 border-white/30 text-white hover:bg-white/10 px-6 lg:px-8 py-4 lg:py-6 text-base lg:text-lg rounded-full backdrop-blur-sm group">
                  <Play className="mr-2 w-5 h-5 group-hover:scale-110 transition-transform" />
                  Upcoming Events
                </Button>
              </Link>
            </div>
          </div>

          {/* Right Visual - Show on mobile too */}
          <div className="relative mt-8 lg:mt-0">
            <div className="relative z-10">
              {/* Main Image Card */}
              <div className="relative rounded-2xl lg:rounded-3xl overflow-hidden shadow-2xl transform lg:rotate-2 lg:hover:rotate-0 transition-transform duration-500 mx-4 lg:mx-0">
                <img
                  src="https://customer-assets.emergentagent.com/job_stemkits-lms/artifacts/cmdkrenz_Gemini_Generated_Image_okx0ghokx0ghokx0.png"
                  alt="Students learning robotics"
                  className="w-full h-[250px] lg:h-[400px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div className="absolute bottom-4 lg:bottom-6 left-4 lg:left-6 right-4 lg:right-6">
                  <p className="text-white font-semibold text-base lg:text-lg">21st Century Skills</p>
                  <p className="text-gray-300 text-xs lg:text-sm">Critical thinking, creativity & teamwork</p>
                </div>
              </div>

              {/* Floating Card 1 - Hidden on mobile */}
              <div className="hidden lg:block absolute -top-8 -left-8 bg-white rounded-2xl p-4 shadow-xl animate-bounce" style={{ animationDuration: '3s' }}>
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                    <span className="text-2xl">🤖</span>
                  </div>
                  <div>
                    <p className="font-bold text-gray-800">2500+</p>
                    <p className="text-sm text-gray-500">Students Certified</p>
                  </div>
                </div>
              </div>

              {/* Floating Card 2 - Hidden on mobile */}
              <div className="hidden lg:block absolute -bottom-4 -right-4 bg-white rounded-2xl p-4 shadow-xl animate-bounce" style={{ animationDuration: '3.5s' }}>
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center">
                    <span className="text-2xl">🎓</span>
                  </div>
                  <div>
                    <p className="font-bold text-gray-800">250+</p>
                    <p className="text-sm text-gray-500">Certified Teachers</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Decorative Elements */}
            <div className="absolute top-1/2 -right-20 w-40 h-40 border-2 border-dashed border-blue-400/30 rounded-full"></div>
            <div className="absolute -bottom-10 left-1/4 w-24 h-24 border-2 border-dashed border-teal-400/30 rounded-full"></div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-2">
          <div className="w-1.5 h-2.5 bg-white/60 rounded-full animate-pulse"></div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
